import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaShoppingCart, FaHeart, FaStar } from 'react-icons/fa';
import { addToCart } from '../../store/slices/cartSlice';
import { toggleCart } from '../../store/slices/uiSlice';
import { toast } from 'react-toastify';

const ProductCard = ({ product }) => {
  const dispatch = useDispatch();
  const tenantState = useSelector((state) => state.tenant);
  const tenant = tenantState.tenant;
  
  // Get tenant from localStorage as fallback
  const getTenantFromStorage = () => {
    try {
      const stored = localStorage.getItem('tenant');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (err) {
      return null;
    }
    return null;
  };
  
  const currentTenant = tenant || getTenantFromStorage();
  const [isLiked, setIsLiked] = useState(false);
  const [sessionLikeCount, setSessionLikeCount] = useState(0);
  
  // Load session-based like status
  useEffect(() => {
    const likedProducts = JSON.parse(localStorage.getItem('likedProducts') || '{}');
    if (likedProducts[product._id]) {
      setIsLiked(true);
      setSessionLikeCount(likedProducts[product._id].count || 0);
    }
  }, [product._id]);

  const currencySymbol = product.currency === 'USD' ? '$' : product.currency === 'EUR' ? '€' : '₹';
  const displayPrice = product.discountedPrice || product.price;
  const imageUrl = product.images && product.images.length > 0 ? product.images[0] : 'https://via.placeholder.com/400';
  const averageRating = product.averageRating || 0;
  const reviewCount = product.reviewCount || 0;
  
  // 🔍 DEBUG: Log image URL for this product
  useEffect(() => {
    if (product && product.images && product.images.length > 0 && imageUrl) {
      console.log(`🖼️  [ProductCard] ${product.name}`);
      console.log(`   Image URL: ${imageUrl}`);
      console.log(`   URL length: ${imageUrl?.length || 0} chars`);
      console.log(`   Is S3: ${imageUrl.includes('amazonaws.com')}`);
      console.log(`   Has pre-signed params: ${imageUrl.includes('X-Amz-Algorithm')}`);
    }
  }, [product, imageUrl]);

  const handleLike = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const likedProducts = JSON.parse(localStorage.getItem('likedProducts') || '{}');
    if (!likedProducts[product._id]) {
      likedProducts[product._id] = { count: 0 };
    }
    
    if (isLiked) {
      likedProducts[product._id].count = Math.max(0, likedProducts[product._id].count - 1);
      if (likedProducts[product._id].count === 0) {
        delete likedProducts[product._id];
        setIsLiked(false);
      }
    } else {
      likedProducts[product._id].count += 1;
      setIsLiked(true);
    }
    
    setSessionLikeCount(likedProducts[product._id]?.count || 0);
    localStorage.setItem('likedProducts', JSON.stringify(likedProducts));
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (product.stock === 0) {
      toast.error('This product is out of stock');
      return;
    }

    // Get tenant ID from Redux or localStorage
    let tenantId = currentTenant?._id || currentTenant?.id;
    
    // If tenant not available, try to load it
    if (!tenantId && !tenantState.loading) {
      try {
        const { loadTenant } = await import('../../store/slices/tenantSlice');
        const result = await dispatch(loadTenant());
        if (result.payload?.tenant) {
          tenantId = result.payload.tenant._id || result.payload.tenant.id;
        }
      } catch (error) {
        console.error('Error loading tenant:', error);
      }
    }
    
    // If still no tenant, try localStorage
    if (!tenantId) {
      const storedTenant = getTenantFromStorage();
      tenantId = storedTenant?._id || storedTenant?.id;
    }
    
    if (!tenantId) {
      toast.error('Unable to identify store. Please refresh the page.');
      return;
    }

    dispatch(addToCart({ product, quantity: 1, tenantId }));
    toast.success(`${product.name} added to cart!`);
    dispatch(toggleCart());
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar
            key={star}
            className={`text-xs ${
              star <= fullStars
                ? 'text-yellow-400 fill-current'
                : star === fullStars + 1 && hasHalfStar
                ? 'text-yellow-400 fill-current opacity-50'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 group relative">
      <Link
        to={`/products/${product._id}`}
        className="block"
      >
      <div className="relative aspect-square overflow-hidden">
        <img
          src={imageUrl}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
        />
        {product.discount > 0 && (
          <span className="absolute top-2 right-2 bg-secondary text-white px-3 py-1 rounded-full text-sm font-semibold">
            {product.discount}% OFF
          </span>
        )}
      </div>
      <div className="p-4">
        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
          {product.category?.name || 'Uncategorized'}
        </p>
        <h3 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-primary transition">
          {product.name}
        </h3>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xl font-bold text-primary">
            {currencySymbol}{displayPrice.toLocaleString()}
          </span>
          {product.discount > 0 && (
            <span className="text-sm text-gray-500 line-through">
              {currencySymbol}{product.price.toLocaleString()}
            </span>
          )}
        </div>
        {/* Rating and Reviews */}
        {(averageRating > 0 || reviewCount > 0) && (
          <div className="flex items-center gap-2 mb-2">
            {renderStars(averageRating)}
            <span className="text-sm text-gray-600">
              {averageRating > 0 ? averageRating.toFixed(1) : '0.0'}
            </span>
            {reviewCount > 0 && (
              <span className="text-xs text-gray-500">
                ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
              </span>
            )}
          </div>
        )}
      </div>
      </Link>
      
      {/* Quick Add to Cart Button */}
      {product.stock > 0 && (
        <button
          onClick={handleAddToCart}
          className="absolute top-2 right-2 bg-primary text-white p-2 rounded-full shadow-lg hover:bg-primary-dark transition opacity-0 group-hover:opacity-100 z-10"
          title="Add to cart"
        >
          <FaShoppingCart />
        </button>
      )}
      
      {/* Like Button */}
      <button
        onClick={handleLike}
        className={`absolute bottom-2 right-2 p-2 rounded-full shadow-lg transition opacity-0 group-hover:opacity-100 z-10 ${
          isLiked
            ? 'bg-red-500 text-white hover:bg-red-600'
            : 'bg-white text-gray-600 hover:bg-gray-100'
        }`}
        title="Like this product"
      >
        <FaHeart className={isLiked ? 'fill-current' : ''} />
      </button>
    </div>
  );
};

export default ProductCard;

